import React, { useState, useEffect } from "react";
import { ref, update } from "firebase/database";
import { db } from "../firebase";
import useFile from "../hooks/useFile";

const EditTeamDetails = ({ gameId, teamId, team, onClose, onSuccess }) => {
  const { uploadImage, uploading, clearError, deleteFile } = useFile();
  const [preview, setPreview] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  
  const [formData, setFormData] = useState({
    teamName: "",
    teamBanner: "",
    fileId: null,
  });

  useEffect(() => {
    if (team) {
      setFormData({
        teamName: team.teamName || "",
        teamBanner: team.teamBanner || "",
        fileId: team.fileId || null,
      });
      if (team.teamBanner) {
        setPreview(team.teamBanner);
      }
    }
  }, [team]);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Show local preview immediately
    const reader = new FileReader();
    reader.onloadend = () => setPreview(reader.result);
    reader.readAsDataURL(selectedFile);

    // Delete old file if exists
    if (formData.fileId) {
      await deleteFile(formData.fileId);
    }

    // Upload new file
    clearError();
    const result = await uploadImage(selectedFile, {
      maxSize: 500 * 1024,
      allowedTypes: ["image/jpeg", "image/png", "image/webp"],
    });

    if (result.success) {
      setFormData((prev) => ({
        ...prev,
        teamBanner: result.fileUrl,
        fileId: result.fileId,
      }));
      setError("");
    } else {
      setError(result.error || "Failed to upload image");
      setPreview(formData.teamBanner || null);
    }
  };

  const handleRemoveImage = async () => {
    if (formData.fileId) {
      setDeleting(true);
      await deleteFile(formData.fileId);
      setDeleting(false);
    }
    setPreview(null);
    setFormData((prev) => ({
      ...prev,
      teamBanner: "",
      fileId: null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.teamName.trim()) {
      setError("Team name is required");
      return;
    }

    setSaving(true);
    setError("");

    try {
      await update(ref(db, `games/${gameId}/teams/${teamId}`), {
        teamName: formData.teamName.trim(),
        teamBanner: formData.teamBanner,
        fileId: formData.fileId,
        updatedAt: Date.now(),
      });

      setSuccess(true);
      if (onSuccess) onSuccess();
      
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Error updating team:", err);
      setError("Failed to update team: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const isDark = document.documentElement.getAttribute('data-theme') === 'dark';

  const styles = {
    container: {
      background: isDark ? "#1e1e2f" : "white",
      borderRadius: "16px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
      overflow: "hidden",
      maxWidth: "500px",
      margin: "0 auto",
    },
    header: {
      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      padding: "24px",
      color: "white",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    headerTitle: {
      margin: 0,
      fontSize: "1.5rem",
      fontWeight: "600",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    closeButton: {
      background: "rgba(255,255,255,0.2)",
      border: "none",
      borderRadius: "50%",
      width: "36px",
      height: "36px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "all 0.2s",
      color: "white",
      fontSize: "1.2rem",
    },
    body: {
      padding: "24px",
      background: isDark ? "#1e1e2f" : "white",
    },
    formGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "600",
      color: isDark ? "#e0e0e0" : "#374151",
      fontSize: "0.95rem",
    },
    required: {
      color: "#ef4444",
      marginLeft: "4px",
    },
    input: {
      width: "100%",
      padding: "12px 16px",
      border: isDark ? "2px solid #4a4a6a" : "2px solid #e5e7eb",
      borderRadius: "10px",
      fontSize: "1rem",
      transition: "all 0.2s",
      outline: "none",
      boxSizing: "border-box",
      background: isDark ? "#2d2d44" : "white",
      color: isDark ? "#e0e0e0" : "#212529",
    },
    uploadArea: {
      border: isDark ? "2px dashed #4a4a6a" : "2px dashed #d1d5db",
      borderRadius: "12px",
      padding: "24px",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.2s",
      background: isDark ? "#2d2d44" : "#f9fafb",
      color: isDark ? "#a0a0a0" : "inherit",
    },
    previewContainer: {
      position: "relative",
      display: "inline-block",
      width: "100%",
    },
    bannerPreview: {
      width: "100%",
      height: "150px",
      objectFit: "cover",
      borderRadius: "12px",
      border: "3px solid #667eea",
    },
    removeButton: {
      position: "absolute",
      top: "-10px",
      right: "-10px",
      background: "#ef4444",
      color: "white",
      border: "none",
      borderRadius: "50%",
      width: "32px",
      height: "32px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 8px rgba(239,68,68,0.4)",
    },
    submitButton: {
      width: "100%",
      padding: "14px",
      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      color: "white",
      border: "none",
      borderRadius: "10px",
      fontSize: "1rem",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      transition: "all 0.2s",
    },
    successMessage: {
      background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      color: "white",
      padding: "16px",
      borderRadius: "10px",
      textAlign: "center",
      marginBottom: "20px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
    },
    errorMessage: {
      background: "#fef2f2",
      color: "#dc2626",
      padding: "12px 16px",
      borderRadius: "10px",
      marginBottom: "20px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.headerTitle}>
          <i className="bi bi-shield-fill"></i>
          Edit Team Details
        </h4>
        <button
          style={styles.closeButton}
          onClick={onClose}
          onMouseOver={(e) => (e.target.style.background = "rgba(255,255,255,0.3)")}
          onMouseOut={(e) => (e.target.style.background = "rgba(255,255,255,0.2)")}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      <div style={styles.body}>
        {success && (
          <div style={styles.successMessage}>
            <i className="bi bi-check-circle-fill fs-5"></i>
            <span>Team updated successfully!</span>
          </div>
        )}

        {error && (
          <div style={styles.errorMessage}>
            <i className="bi bi-exclamation-circle-fill"></i>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>
              <i className="bi bi-shield me-2"></i>
              Team Name
              <span style={styles.required}>*</span>
            </label>
            <input
              type="text"
              style={styles.input}
              value={formData.teamName}
              onChange={(e) => setFormData({ ...formData, teamName: e.target.value })}
              placeholder="Enter team name"
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>
              <i className="bi bi-image me-2"></i>
              Team Banner
            </label>

            {preview ? (
              <div style={styles.previewContainer}>
                <img src={preview} alt="Banner Preview" style={styles.bannerPreview} />
                <button
                  type="button"
                  style={styles.removeButton}
                  onClick={handleRemoveImage}
                  disabled={deleting}
                  title="Remove banner"
                >
                  {deleting ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    <i className="bi bi-x-lg"></i>
                  )}
                </button>
                {uploading && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "10px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      background: "rgba(0,0,0,0.7)",
                      color: "white",
                      padding: "6px 16px",
                      borderRadius: "20px",
                      fontSize: "0.85rem",
                    }}
                  >
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Uploading...
                  </div>
                )}
              </div>
            ) : (
              <label style={styles.uploadArea}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  disabled={uploading}
                />
                {uploading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="bi bi-cloud-arrow-up fs-1 text-muted d-block mb-2"></i>
                    <span className="text-muted">Click to upload banner image</span>
                    <small className="d-block text-muted mt-1">
                      JPG, PNG or WebP (Max 500KB)
                    </small>
                  </>
                )}
              </label>
            )}
          </div>

          <button
            type="submit"
            style={{
              ...styles.submitButton,
              opacity: saving || uploading ? 0.7 : 1,
            }}
            disabled={saving || uploading}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle-fill"></i>
                Save Changes
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditTeamDetails;
